import React, { useState } from 'react';
import axios from 'axios';
import {
  Box, Button, TextField, Typography, Paper, CircularProgress,
  Collapse, Chip, Tooltip, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, IconButton, Alert,
  ToggleButtonGroup, ToggleButton, Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SUMMARY_URL = `${BACKEND_URL}/api/report/order-routing/summary`;
const ORDERS_URL  = `${BACKEND_URL}/api/report/order-routing/orders`;

function today() {
  return new Date().toISOString().slice(0, 10);
}

// ── Small stat card ─────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = 'inherit' }) {
  return (
    <Paper sx={{ p: 2, textAlign: 'center', minWidth: 130 }}>
      <Typography variant="h4" fontWeight="bold" color={color}>{value ?? '—'}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      {sub && <Typography variant="caption" display="block" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

// ── Match chip ───────────────────────────────────────────────────────────────
function MatchChip({ isMatch, hasSuggestion }) {
  if (!hasSuggestion) return <Chip label="ไม่มีคำแนะนำ" size="small" variant="outlined" />;
  return isMatch
    ? <Chip label="✓ ตรง" size="small" color="success" />
    : <Chip label="✗ ต่าง" size="small" color="error" />;
}

// ── Drill-down: individual orders ────────────────────────────────────────────
function OrdersTable({ date, orderByOptionId, onlyMismatch }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded]   = useState(false);

  const load = async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const res = await axios.post(ORDERS_URL, { date, orderByOptionId, onlyMismatch });
      setData(res.data);
      setLoaded(true);
    } catch {
      setData({ orders: [], total: 0 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {!loaded && (
        <Button size="small" onClick={load} disabled={loading} startIcon={loading ? <CircularProgress size={14} /> : null}>
          {loading ? 'กำลังโหลด...' : 'โหลดรายการ Order'}
        </Button>
      )}
      {loaded && data && (
        <>
          <Typography variant="caption" color="text.secondary" mb={1} display="block">
            {data.total} รายการ {data.total >= 500 ? '(แสดงสูงสุด 500)' : ''}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '11%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '17%' }} />
                <col style={{ width: '4.5%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '9.5%' }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell>เวลา (ไทย)</TableCell>
                  <TableCell>ลูกค้า</TableCell>
                  <TableCell>ชื่อสินค้า</TableCell>
                  <TableCell align="center" sx={{ px: 0.5 }}>ลิงก์</TableCell>
                  <TableCell>เว็บ</TableCell>
                  <TableCell align="right">ยอด (¥)</TableCell>
                  <TableCell>ชำระ</TableCell>
                  <TableCell>แนะนำ</TableCell>
                  <TableCell>เลือกจริง</TableCell>
                  <TableCell align="center">ผล</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.orders.map(o => (
                  <TableRow
                    key={o.id}
                    sx={{ bgcolor: o.hasSuggestion && !o.isMatch ? '#fff3f3' : 'inherit' }}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12, verticalAlign: 'top' }}>{o.createdAtThai}</TableCell>
                    <TableCell sx={{ fontSize: 12, wordBreak: 'break-word', verticalAlign: 'top' }}>
                      {o.customerName || '—'}
                      {o.customerContact && (
                        <Typography variant="caption" display="block" color="text.secondary">{o.customerContact}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, wordBreak: 'break-word', verticalAlign: 'top' }}>
                      {o.smileNameInput || <span style={{ color: '#bbb' }}>—</span>}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 12, px: 0.5, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      {o.url ? (
                        <Tooltip title={o.url} placement="left">
                          <span>
                            <Link href={o.url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 12 }}>
                              ลิงก์
                            </Link>
                          </span>
                        </Tooltip>
                      ) : <span style={{ color: '#bbb' }}>—</span>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, wordBreak: 'break-word', verticalAlign: 'top' }}>{o.website || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, verticalAlign: 'top' }}>
                      ¥{(o.totalJpy || 0).toLocaleString()}
                      {o.qty > 1 && <Typography variant="caption" display="block" color="text.secondary">×{o.qty}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, verticalAlign: 'top' }}>
                      {o.isSeven ? <Chip label="7-11" size="small" color="warning" /> : <span style={{ color: '#aaa' }}>Merpay</span>}
                      {o.isHandCarry && <Chip label="Air" size="small" sx={{ ml: 0.5 }} />}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, wordBreak: 'break-word', verticalAlign: 'top' }}>
                      {o.suggestedOrderer?.label ?? <span style={{ color: '#bbb' }}>—</span>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, wordBreak: 'break-word', verticalAlign: 'top' }}>{o.actualOrderer?.label ?? '—'}</TableCell>
                    <TableCell align="center" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}><MatchChip isMatch={o.isMatch} hasSuggestion={o.hasSuggestion} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

// ── Per-day row with orderer breakdown ───────────────────────────────────────
function DayRow({ day, onlyMismatch }) {
  const [open, setOpen] = useState(false);
  const [openOrdererId, setOpenOrdererId] = useState(null);

  const matchRateColor = day.matchRate == null ? 'text.secondary'
    : day.matchRate >= 70 ? 'success.main'
    : day.matchRate >= 40 ? 'warning.main'
    : 'error.main';

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& td': { fontWeight: open ? 700 : 400 } }}
        onClick={() => setOpen(o => !o)}
      >
        <TableCell>
          <IconButton size="small">{open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>
        </TableCell>
        <TableCell>{day.date}</TableCell>
        <TableCell align="right">{day.total}</TableCell>
        <TableCell align="right">
          {day.withSuggestion}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            ({day.total > 0 ? Math.round(day.withSuggestion / day.total * 100) : 0}%)
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Box sx={{ color: matchRateColor, fontWeight: 700 }}>
            {day.matchRate != null ? `${day.matchRate}%` : '—'}
          </Box>
        </TableCell>
        <TableCell align="right">
          {day.withSuggestion - day.matchCount}
          {day.withSuggestion > 0 && (
            <Typography variant="caption" color="error.main" sx={{ ml: 0.5 }}>
              ({Math.round((day.withSuggestion - day.matchCount) / day.withSuggestion * 100)}%)
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {day.byActual.map(a => (
              <Tooltip key={a.id ?? 'none'} title={`¥${(a.totalJpy || 0).toLocaleString()}`}>
                <Chip label={`${a.label}: ${a.count}`} size="small" variant="outlined" />
              </Tooltip>
            ))}
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, bgcolor: '#fafafa' }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" mb={1}>Breakdown ตาม Orderer ที่เลือกจริง</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Orderer จริง</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">ยอดรวม (¥)</TableCell>
                    <TableCell>ดูรายการ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {day.byActual.map(a => (
                    <React.Fragment key={a.id ?? 'none'}>
                      <TableRow>
                        <TableCell>
                          <Typography variant="body2">{a.label}</Typography>
                          {a.type && <Typography variant="caption" color="text.secondary">{a.type}</Typography>}
                        </TableCell>
                        <TableCell align="right">{a.count}</TableCell>
                        <TableCell align="right">¥{(a.totalJpy || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setOpenOrdererId(v => v === a.id ? null : a.id)}
                          >
                            {openOrdererId === a.id ? 'ซ่อน' : 'ดู Order'}
                          </Button>
                        </TableCell>
                      </TableRow>
                      {openOrdererId === a.id && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ bgcolor: '#f5f5f5' }}>
                            <OrdersTable date={day.date} orderByOptionId={a.id} onlyMismatch={onlyMismatch} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>

              {/* All orders for this day */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" mb={1}>ทุก Order ในวันนี้</Typography>
                <OrdersTable date={day.date} onlyMismatch={onlyMismatch} />
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Main report ───────────────────────────────────────────────────────────────
export default function OrderRoutingReport() {
  const [startDate, setStartDate] = useState(today());
  const [endDate,   setEndDate]   = useState(today());
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [onlyMismatch, setOnlyMismatch] = useState(false);
  const [tab, setTab] = useState('daily'); // 'daily' | 'matrix'

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(SUMMARY_URL, { startDate, endDate });
      setData(res.data);
    } catch {
      setError('โหลดรายงานไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  const { summary, byDate, matrix } = data || {};

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto', p: 2 }}>
      <Typography variant="h5" fontWeight="bold" mb={2}>
        📊 Order Routing Report
      </Typography>

      {/* Date picker */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="วันเริ่ม" type="date" size="small"
          value={startDate} onChange={e => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 170 }}
        />
        <TextField
          label="วันสิ้นสุด" type="date" size="small"
          value={endDate} onChange={e => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 170 }}
        />
        <Button variant="contained" onClick={fetchReport} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'โหลดรายงาน'}
        </Button>
        <Tooltip title="แสดงเฉพาะ Order ที่ admin เลือกต่างจากคำแนะนำ (ใน drill-down)">
          <Button
            size="small"
            variant={onlyMismatch ? 'contained' : 'outlined'}
            color="warning"
            startIcon={<FilterListIcon />}
            onClick={() => setOnlyMismatch(v => !v)}
          >
            เฉพาะ Mismatch
          </Button>
        </Tooltip>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary cards */}
      {summary && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <StatCard label="Order ทั้งหมด" value={summary.totalOrders} />
          <StatCard
            label="มีคำแนะนำ"
            value={summary.withSuggestion}
            sub={`${summary.totalOrders > 0 ? Math.round(summary.withSuggestion / summary.totalOrders * 100) : 0}% ของทั้งหมด`}
            color="primary.main"
          />
          <StatCard
            label="Match rate"
            value={summary.matchRate != null ? `${summary.matchRate}%` : '—'}
            sub={`${summary.matchCount} / ${summary.withSuggestion} ที่มีคำแนะนำ`}
            color={summary.matchRate >= 70 ? 'success.main' : summary.matchRate >= 40 ? 'warning.main' : 'error.main'}
          />
          <StatCard
            label="Admin เลือกต่าง"
            value={summary.mismatchCount}
            sub={summary.withSuggestion > 0 ? `${Math.round(summary.mismatchCount / summary.withSuggestion * 100)}% ของที่มีคำแนะนำ` : undefined}
            color={summary.mismatchCount > 0 ? 'error.main' : 'inherit'}
          />
          <StatCard label="ไม่มีคำแนะนำ" value={summary.noSuggestion} sub="(Order เก่าก่อนเปิดระบบ)" />
        </Box>
      )}

      {/* Tab toggle */}
      {data && (
        <ToggleButtonGroup
          value={tab} exclusive size="small"
          onChange={(_, v) => v && setTab(v)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="daily">รายวัน</ToggleButton>
          <ToggleButton value="matrix">Confusion Matrix</ToggleButton>
        </ToggleButtonGroup>
      )}

      {/* Daily breakdown table */}
      {data && tab === 'daily' && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell width={40} />
                <TableCell>วันที่</TableCell>
                <TableCell align="right">Orders</TableCell>
                <TableCell align="right">มีคำแนะนำ</TableCell>
                <TableCell align="right">Match %</TableCell>
                <TableCell align="right">Mismatch</TableCell>
                <TableCell>Orderer จริง (breakdown)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byDate?.map(day => (
                <DayRow key={day.date} day={day} onlyMismatch={onlyMismatch} />
              ))}
              {byDate?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: '#aaa', py: 4 }}>ไม่มีข้อมูลในช่วงวันที่เลือก</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confusion matrix */}
      {data && tab === 'matrix' && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            แนะนำ → เลือกจริง (Confusion Matrix)
          </Typography>
          <Typography variant="caption" color="text.secondary" mb={2} display="block">
            แถว = ระบบแนะนำ, คอลัมน์ = admin เลือกจริง — เซลล์แนวทแยงคือ Match
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>แนะนำ ↓ / จริง →</TableCell>
                  {[...new Set(matrix?.map(m => m.actualLabel))].map(l => (
                    <TableCell key={l} align="right">{l}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...new Set(matrix?.map(m => m.suggestedLabel))].map(sug => (
                  <TableRow key={sug}>
                    <TableCell sx={{ fontWeight: 600 }}>{sug}</TableCell>
                    {[...new Set(matrix?.map(m => m.actualLabel))].map(act => {
                      const cell = matrix.find(m => m.suggestedLabel === sug && m.actualLabel === act);
                      const isMatch = sug === act;
                      return (
                        <TableCell
                          key={act} align="right"
                          sx={{ bgcolor: isMatch ? '#e8f5e9' : cell ? '#ffebee' : 'inherit', fontWeight: cell ? 600 : 400 }}
                        >
                          {cell?.count ?? '—'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
